<template>
  <div class="flight-container">
    <div ref="chart" class="plot"></div>
    <div ref="map" class="map">
      <div ref="altitudeChart" class="altitude-chart"></div>
    </div>
  </div>
</template>

<script>
// import Plotly from 'plotly.js';
// Plotly + modules minimaux
import Plotly from 'plotly.js-dist-min';

import L from 'leaflet';
import axios from 'axios';

export default {
  name: 'FlightPlotWithMap',
  props: {
    csvUrl: { type: String, required: true }
  },
  data() {
    return {
      map: null,
      marker: null,
      flightPath: null,
      altitudeTrace: null,
      flightData: [],
      altitudes: [],
      altitudeGraph: null
    };
  },
  async mounted() {
    const planeIcon = L.icon({
      iconUrl: 'https://observations.ipsl.fr/aeris/magic/windy/img/marker-plane.png',
      iconSize: [32, 32],      // taille de l'icône
      iconAnchor: [16, 32],    // point de l'icône qui correspond à la position du marker
      // popupAnchor: [1, -34],   // point de popup si besoin
      // shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
      // shadowSize: [32, 32]
    });


    try {
      const response = await axios.get(this.csvUrl);
      const raw = String(response.data || '').trim();
      if (!raw) throw new Error('CSV vide');

      const lines = raw.split(/\r?\n/).filter(l => l.trim() !== '');
      const headers = lines[0].split(',').map(h => h.replace(/^"+|"+$/g, '').trim());
      const rows = lines.slice(1).map(line =>
        line.split(',').map(v => (v || '').replace(/^"+|"+$/g, '').trim())
      );

      // --- parse time & alt ---
      const parseTimeMs = (s) => {
        const d = new Date(s);
        return isNaN(d.getTime()) ? null : d.getTime();
      };

      const timeMs = rows.map(r => parseTimeMs(r[0]));
      const lat = rows.map(r => parseFloat(r[2]));
      const lon = rows.map(r => parseFloat(r[3]));
      const alt = rows.map(r => parseFloat(r[1])); // altitude = 2e colonne
      const col7to11 = [6, 7, 8, 9, 10];

      const validIdx = rows.map((_, i) =>
        timeMs[i] !== null && !isNaN(lat[i]) && !isNaN(lon[i]) ? i : null
      ).filter(i => i !== null);

      const t = validIdx.map(i => timeMs[i]);
      const latF = validIdx.map(i => lat[i]);
      const lonF = validIdx.map(i => lon[i]);
      const altF = validIdx.map(i => alt[i]);
      const timeLabel = t.map(ms => new Date(ms).toISOString().replace('T', ' ').split('.')[0]);

      this.flightData = validIdx.map((i, k) => ({
        idx: k,
        timeMs: t[k],
        lat: latF[k],
        lon: lonF[k],
        alt: altF[k]
      }));

      // --- Plot principal (compact) ---
      const traces = col7to11
        .filter(c => c < headers.length)
        .map(c => ({
          x: t,
          y: validIdx.map(i => parseFloat(rows[i][c])),
          type: 'scatter',
          mode: 'lines',
          name: headers[c] || `Col ${c + 1}`,
          text: timeLabel,
          hovertemplate: '%{text}<br>%{y}<extra></extra>'
        }));

      const layout = {
        title: { text: 'Gerfaut - Picarro', font: { size: 16 } },
        margin: { t: 30, r: 10, b: 30, l: 40 },
        xaxis: { title: '', type: 'date', automargin: true, showticklabels: true },
        yaxis: { title: '', automargin: true },
        height: 300
      };

      Plotly.newPlot(this.$refs.chart, traces, layout, { responsive: true });

      // --- Leaflet map ---
      this.map = L.map(this.$refs.map).setView([latF[0], lonF[0]], 7);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(this.map);

      const latlngs = latF.map((la, i) => [la, lonF[i]]);
      this.flightPath = L.polyline(latlngs, { color: 'blue', weight: 3 }).addTo(this.map);
      this.map.fitBounds(this.flightPath.getBounds());

      // this.marker = L.marker(latlngs[0]).addTo(this.map);
      this.marker = L.marker(latlngs[0], { icon: planeIcon }).addTo(this.map);

      // --- Mini graphique d'altitude ---
      this.altitudes = altF;
      const dist = latF.map((_, i) => i); // index pour l'abscisse (simple)
      const altTrace = {
        x: dist,
        y: altF,
        type: 'scatter',
        mode: 'lines',
        line: { color: 'orange' },
        name: 'Altitude',
        hovertemplate: 'Index %{x}<br>Alt %{y} m<extra></extra>'
      };
      const altLayout = {
        margin: { t: 20, r: 10, b: 20, l: 40 },
        xaxis: { title: '', showticklabels: false },
        yaxis: { title: 'Altitude (m)', titlefont: { size: 10 } },
        height: 120,
        paper_bgcolor: 'rgba(255,255,255,0.8)',
        plot_bgcolor: 'rgba(255,255,255,0.8)'
      };
      Plotly.newPlot(this.$refs.altitudeChart, [altTrace], altLayout, { displayModeBar: false });
      this.altitudeGraph = this.$refs.altitudeChart;

      // --- Synchronisation : survol du graph principal ---
      this.$refs.chart.on('plotly_hover', (ev) => {
        const idx = ev.points[0].pointIndex;
        const p = this.flightData[idx];
        if (p) {
          this.marker.setLatLng([p.lat, p.lon]);
          // Mettre à jour le curseur sur le mini graphe
          Plotly.Fx.hover(this.altitudeGraph, [{ curveNumber: 0, pointNumber: idx }]);
        }
      });

    } catch (err) {
      console.error('Erreur de chargement CSV / parsing :', err);
    }
  }
};
</script>

<style scoped>
.flight-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.plot {
  width: 100%;
  height: 300px;
}
.map {
  position: relative;
  width: 100%;
  height: 400px;
  border: 1px solid #ccc;
  border-radius: 10px;
}
.altitude-chart {
  position: absolute;
  bottom: 10px;
  left: 10px;
  width: 250px;
  height: 120px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #ccc;
  border-radius: 8px;
  z-index: 500;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}
</style>
